import { MouseEvent, useEffect, useState } from 'react';
import { addOrUpdateCart, deleteProductCart } from '../../../api/cart';
import { useDispatch, useSelector } from 'react-redux';

import { Anchor } from '../../../shared/Anchor';
import type { DishAttrType } from '../../../types/product';
import { QuantityButton } from '../../../shared/QuantityButton';
import { setCart } from '../../../store/cart/cartSlice';
import type { state } from '../../../types/store/state';
import { useTranslation } from 'next-i18next';
import { handleToastAsyncAction } from '../../../utils/handleToastAsyncAction';
import * as Sentry from '@sentry/react';

interface Props {
  orderBranchProductId?: number;
  storeBranchProductId?: number;
  customQuantity?: number;
  added?: boolean;
  dishAttributes?: DishAttrType;
  observation?: string;
  isDish?: boolean;
  productUrl?: number;
  setDishAttributes?: (attr: DishAttrType) => void;
  setObservation?: (attr: string) => void;
  showQuantity?: boolean;
  productName: string;
}

export const AddToCart = (props: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const cartReference = useSelector((state: state) => state.cart.reference);

  const [count, setCount] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    if (props.customQuantity) {
      setCount(props.customQuantity);
    }
    if (props.added) {
      setAdded(props.added);
    }
  }, [props.customQuantity, props.added]);

  const handleCart = async (event: MouseEvent, count: number = 1) => {
    event.preventDefault();
    setAdded(true);
    let cart;
    let paramsToSend = {
      storeBranchProduct: props.storeBranchProductId,
      quantity: count,
      observation: props.observation,
      attributes: props.dishAttributes || {},
    };
    let orderBranchHandleParam = {
      orderBranchProduct: props.orderBranchProductId,
    };

    if (props.isDish) {
      if (count === 0) {
        delete paramsToSend.storeBranchProduct;
      }
      paramsToSend = {
        ...paramsToSend,
        ...orderBranchHandleParam,
      };
    }

    if (count > 0) {
      cart = await addOrUpdateCart({
        id: cartReference,
        params: paramsToSend,
      });

      if (
        props.setDishAttributes !== undefined &&
        props.setObservation !== undefined
      ) {
        props.setDishAttributes({});
        props.setObservation('');
      }
    } else {
      cart = await handleToastAsyncAction(
        () => deleteProductCart({
          id: cartReference,
          params: paramsToSend,
        }),
        {
          onSuccessMessage: t('cart.product.delete.toastify.success', {
            productName: props.productName,
          }),
          onErrorMessage: t('cart.product.delete.toastify.error', {
            productName: props.productName,
          }),
          dispatchAction: () => {
            setAdded(false);
            setCount(1);
          },
          errorLogger: (error) => {
            console.error('Error deleting product from cart:', error);
            Sentry.captureException(error, {
              tags: {
                action: 'delete_product_cart',
                cartReference,
                orderBranchProductId: props.orderBranchProductId,
              },
            });
          },
        }
      );

      if (cart.success && cart.data) {
        dispatch(setCart(cart.data));
        return;
      }
    }

    dispatch(setCart(cart));
  };

  if (!added || props.customQuantity === 0) {
    return (
      <>
        <Anchor
          title={t('product.card.cta.details.title')}
          url={`/product/${props.productUrl}/`}
          size="medium"
          variation="primary"
        />
      </>
    );
  }

  return (
    <>
      {props.isDish && props.showQuantity === true ? (
        <Anchor
          title={t('product.card.cta.details.title')}
          url={`/product/${props.productUrl}/`}
          size="medium"
          variation="primary"
          productClass={true}
        />
      ) : (
        <QuantityButton
          handleAddToCart={handleCart}
          setCount={setCount}
          count={count}
          setAdded={setAdded}
        />
      )}
    </>
  );
};
